<template>
  <div class="template-editor">
    <!-- 顶部导航栏 -->
    <AppHeader 
      @import-template="handleImportTemplate"
      @export-template="handleQuickSave"
      @undo="handleUndo"
      @redo="handleRedo"
      @zoom-change="handleZoomChange"
      @close="handleCloseEditor"
    />
    
    <!-- 主内容区域 -->
    <div class="main-content">
      <!-- 左侧字段选择面板 -->
      <div class="left-panel" :class="{ collapsed: leftPanelCollapsed }">
        <div class="panel-header">
          <h3 class="panel-title">
            <span class="title-icon">📋</span>
            字段选择
          </h3>
          <button 
            @click="toggleLeftPanel" 
            class="collapse-btn"
            :title="leftPanelCollapsed ? '展开字段面板' : '折叠字段面板'"
          >
            {{ leftPanelCollapsed ? '📂' : '📁' }}
          </button>
        </div>
        <div class="panel-content" v-if="!leftPanelCollapsed">
          <FieldSelector 
            @add-field="handleAddField" 
            @remove-field="handleRemoveField"
          />
        </div>
      </div>
      
      <!-- 中间画布区域 -->
      <div class="center-panel">
        <CanvasContainer ref="canvasContainer" />
      </div>
      
      <!-- 右侧配置面板 -->
      <div class="right-panel" :class="{ collapsed: rightPanelCollapsed }">
        <div class="panel-header">
          <h3 class="panel-title">
            <span class="title-icon">⚙️</span>
            配置面板
          </h3>
          <button 
            @click="toggleRightPanel" 
            class="collapse-btn"
            :title="rightPanelCollapsed ? '展开配置面板' : '折叠配置面板'"
          >
            {{ rightPanelCollapsed ? '📂' : '📁' }}
          </button>
        </div>
        <div class="panel-content" v-if="!rightPanelCollapsed">
          <ConfigPanel />
        </div>
      </div>
    </div>
    
    <!-- 浮动操作按钮 -->
    <div class="floating-actions">
      <FloatingButton
        icon="👁️"
        text="预览"
        color="#17a2b8"
        @click="handlePreviewPrint"
      />
      <FloatingButton
        icon="🖨️"
        text="打印"
        color="#28a745"
        @click="handlePrintTemplate"
      />
    </div>
    
    <!-- 提示消息 -->
    <transition name="toast">
      <div v-if="toast.visible" :class="['toast', toast.type]">
        <span class="toast-icon">{{ toast.icon }}</span>
        <span class="toast-message">{{ toast.message }}</span>
        <button @click="hideToast" class="toast-close">✕</button>
      </div>
    </transition>
    
    <!-- 加载遮罩 -->
    <transition name="loading">
      <div v-if="loading" class="loading-overlay">
        <div class="loading-spinner">
          <div class="spinner"></div>
          <div class="loading-text">{{ loadingText }}</div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { ref, reactive, onMounted, onUnmounted } from 'vue'
import { useStore } from 'vuex'
import AppHeader from '@/components/layout/AppHeader.vue'
import FieldSelector from '@/components/business/FieldSelector.vue'
import CanvasContainer from '@/components/business/CanvasContainer.vue'
import ConfigPanel from '@/components/business/ConfigPanel.vue'
import FloatingButton from '@/components/base/FloatingButton.vue'
import { TemplateService } from '@/services/templateService'
import { FieldService } from '@/services/fieldService'
import lodopManager from '@/utils/lodop'

export default {
  name: 'TemplateEditor',
  components: {
    AppHeader,
    FieldSelector,
    CanvasContainer,
    ConfigPanel,
    FloatingButton
  },
  
  setup() {
    const store = useStore()
    
    // 引用
    const canvasContainer = ref(null)
    
    // 本地状态
    const leftPanelCollapsed = ref(false)
    const rightPanelCollapsed = ref(false)
    const loading = ref(false)
    const loadingText = ref('处理中...')
    
    // 提示消息
    const toast = reactive({
      visible: false,
      type: 'success',
      icon: '✓',
      message: '',
      timer: null
    })
    
    // 方法
    const toggleLeftPanel = () => {
      leftPanelCollapsed.value = !leftPanelCollapsed.value
      // 保存到本地存储
      localStorage.setItem('leftPanelCollapsed', leftPanelCollapsed.value)
    }
    
    const toggleRightPanel = () => {
      rightPanelCollapsed.value = !rightPanelCollapsed.value
      // 保存到本地存储
      localStorage.setItem('rightPanelCollapsed', rightPanelCollapsed.value)
    }
    
    const handleAddField = (field) => {
      try {
        if (field.category === 'table') {
          // 处理表格字段
          const tableElement = store.state.canvas.elements.find(el => el.type === 'table')
          if (tableElement) {
            // 添加到现有表格
            addColumnToExistingTable(tableElement, field)
          } else {
            // 创建新表格
            const tableEl = FieldService.createTableElement([field])
            store.dispatch('canvas/addElement', tableEl)
          }
        } else {
          // 创建文本元素
          const textElement = FieldService.createTextElement(field)
          store.dispatch('canvas/addElement', textElement)
        }
        
        showToast('success', '✓', `已添加字段: ${field.displayName || field.name}`)
      } catch (error) {
        showToast('error', '❌', `添加字段失败: ${error.message}`)
      }
    }
    
    const handleRemoveField = (field) => {
      try {
        const elements = store.state.canvas.elements
        const elementsToRemove = []
        
        // 查找需要删除的元素
        elements.forEach((element, index) => {
          if (field.category === 'table') {
            // 处理表格字段
            if (element.type === 'table' && element.columns) {
              const columnIndex = element.columns.findIndex(col => col.id === field.id)
              if (columnIndex !== -1) {
                if (element.columns.length === 1) {
                  // 如果是表格的最后一列，删除整个表格
                  elementsToRemove.push(index)
                } else {
                  // 删除表格中的列
                  removeColumnFromTable(element, field, index)
                }
              }
            }
          } else {
            // 处理文本元素
            if (element.fieldId === field.id) {
              elementsToRemove.push(index)
            }
          }
        })
        
        // 从后往前删除元素（避免索引问题）
        elementsToRemove.reverse().forEach(index => {
          store.dispatch('canvas/removeElement', index)
        })
        
        if (elementsToRemove.length > 0) {
          showToast('success', '✓', `已删除字段: ${field.displayName || field.name}`)
        }
      } catch (error) {
        showToast('error', '❌', `删除字段失败: ${error.message}`)
      }
    }
    
    const addColumnToExistingTable = (tableElement, field) => {
      const elements = store.state.canvas.elements
      const tableIndex = elements.indexOf(tableElement)
      
      if (tableIndex === -1) return
      
      const updatedTable = { ...tableElement }
      
      // 检查是否已存在该字段
      if (updatedTable.columns.find(col => col.id === field.id)) {
        showToast('warning', '⚠️', '该字段已存在于表格中')
        return
      }
      
      // 添加新列
      updatedTable.columns.push({
        id: field.id,
        title: field.displayName || field.name,
        width: FieldService.getDefaultColumnWidth(field.id)
      })
      
      // 更新表格数据
      updatedTable.data = FieldService.generateMockTableData(updatedTable.columns)
      
      store.dispatch('canvas/updateElement', { 
        index: tableIndex, 
        element: updatedTable 
      })
    }
    
    const removeColumnFromTable = (tableElement, field, tableIndex) => {
      const updatedTable = { ...tableElement }
      
      // 删除指定列
      updatedTable.columns = updatedTable.columns.filter(col => col.id !== field.id)
      
      // 更新表格数据
      updatedTable.data = FieldService.generateMockTableData(updatedTable.columns)
      
      store.dispatch('canvas/updateElement', { 
        index: tableIndex, 
        element: updatedTable 
      })
    }
    
    const handlePreviewPrint = async () => {
      try {
        loading.value = true
        loadingText.value = '正在生成预览...'
        
        const elements = store.state.canvas.elements
        if (elements.length === 0) {
          showToast('warning', '⚠️', '模板中没有元素，请先添加字段')
          return
        }
        
        // 检查Lodop服务
        const isServiceReady = await lodopManager.checkServiceStatus()
        if (!isServiceReady) {
          showToast('error', '❌', 'Lodop服务未启动，请先安装并启动Lodop打印服务')
          return
        }
        
        const LODOP = lodopManager.createPrintTask()
        if (!LODOP) {
          showToast('error', '❌', 'Lodop插件未加载，请检查服务是否正常运行')
          return
        }
        
        // 生成打印内容
        loadingText.value = '正在准备打印内容...'
        lodopManager.generatePrintContent(LODOP, store.state.template.config, elements)
        
        // 显示预览
        LODOP.PREVIEW()
        showToast('success', '✓', '预览生成成功')
        
      } catch (error) {
        console.error('预览打印失败:', error)
        showToast('error', '❌', `预览失败: ${error.message}`)
      } finally {
        loading.value = false
      }
    }
    
    const handlePrintTemplate = async () => {
      try {
        loading.value = true
        loadingText.value = '正在准备打印...'
        
        const elements = store.state.canvas.elements
        if (elements.length === 0) {
          showToast('warning', '⚠️', '模板中没有元素，请先添加字段')
          return
        }
        
        const isServiceReady = await lodopManager.checkServiceStatus()
        if (!isServiceReady) {
          showToast('error', '❌', 'Lodop服务未启动，请先安装并启动Lodop打印服务')
          return
        }
        
        const LODOP = lodopManager.createPrintTask()
        if (!LODOP) {
          showToast('error', '❌', 'Lodop插件未加载，请检查服务是否正常运行')
          return
        }
        
        lodopManager.generatePrintContent(LODOP, store.state.template.config, elements)
        LODOP.PRINT()
        
        showToast('success', '✓', '已发送到打印机')
        
      } catch (error) {
        console.error('打印失败:', error)
        showToast('error', '❌', `打印失败: ${error.message}`)
      } finally {
        loading.value = false
      }
    }
    
    const handleCloseEditor = () => {
      if (confirm('确定要关闭编辑器吗？未保存的更改将丢失。')) {
        // 可以添加保存提醒逻辑
        window.close()
      }
    }
    
    // 导入模板
    const handleImportTemplate = () => {
      const input = document.createElement('input')
      input.type = 'file'
      input.accept = '.json'
      input.onchange = async (e) => {
        const file = e.target.files[0]
        if (file) {
          try {
            const templateData = await TemplateService.importTemplate(file)
            store.dispatch('template/setConfig', templateData.config)
            store.dispatch('canvas/setElements', templateData.elements)
            showToast('success', '✓', '模板导入成功')
          } catch (error) {
            showToast('error', '❌', `导入失败: ${error.message}`)
          }
        }
      }
      input.click()
    }
    
    // 撤销重做操作
    const handleUndo = () => {
      store.dispatch('canvas/undo')
      showToast('info', '↶', '已撤销操作')
    }
    
    const handleRedo = () => {
      store.dispatch('canvas/redo')
      showToast('info', '↷', '已重做操作')
    }
    
    // 缩放控制
    const handleZoomChange = (zoomLevel) => {
      store.dispatch('canvas/setZoomLevel', zoomLevel)
    }
    
    const handleQuickSave = () => {
      try {
        const templateConfig = store.state.template.config
        const elements = store.state.canvas.elements
        
        // 验证模板
        const validation = TemplateService.validateTemplate(templateConfig)
        if (!validation.isValid) {
          showToast('error', '❌', `模板验证失败: ${validation.errors[0]}`)
          return
        }
        
        TemplateService.exportTemplate(templateConfig, elements)
        showToast('success', '✓', '模板保存成功')
        
      } catch (error) {
        showToast('error', '❌', `保存失败: ${error.message}`)
      }
    }
    
    const showToast = (type, icon, message) => {
      // 清除之前的定时器
      if (toast.timer) {
        clearTimeout(toast.timer)
      }
      
      toast.type = type
      toast.icon = icon
      toast.message = message
      toast.visible = true
      
      // 自动隐藏
      toast.timer = setTimeout(() => {
        hideToast()
      }, 3000)
    }
    
    const hideToast = () => {
      toast.visible = false
      if (toast.timer) {
        clearTimeout(toast.timer)
        toast.timer = null
      }
    }
    
    // 键盘快捷键
    const handleKeyDown = (event) => {
      if (event.ctrlKey || event.metaKey) {
        switch (event.key) {
          case 's':
            event.preventDefault()
            handleQuickSave()
            break
          case 'p':
            event.preventDefault()
            handlePreviewPrint()
            break
          case '1':
            event.preventDefault()
            toggleLeftPanel()
            break
          case '2':
            event.preventDefault()
            toggleRightPanel()
            break
        }
      }
    }
    
    // 生命周期
    onMounted(() => {
      // 恢复面板状态
      const leftCollapsed = localStorage.getItem('leftPanelCollapsed')
      const rightCollapsed = localStorage.getItem('rightPanelCollapsed')
      
      if (leftCollapsed !== null) {
        leftPanelCollapsed.value = leftCollapsed === 'true'
      }
      
      if (rightCollapsed !== null) {
        rightPanelCollapsed.value = rightCollapsed === 'true'
      }
      
      // 添加键盘事件监听
      document.addEventListener('keydown', handleKeyDown)
      
      // 检查Lodop服务状态
      checkLodopStatus()
      
      // 显示欢迎提示
      setTimeout(() => {
        showToast('info', 'ℹ️', '欢迎使用打印模板编辑器！支持快捷键操作。')
      }, 1000)
    })
    
    onUnmounted(() => {
      document.removeEventListener('keydown', handleKeyDown)
      if (toast.timer) {
        clearTimeout(toast.timer)
      }
    })
    
    const checkLodopStatus = async () => {
      try {
        const isReady = await lodopManager.checkServiceStatus()
        if (!isReady) {
          showToast('warning', '⚠️', 'Lodop打印服务未启动，打印功能将受限')
        }
      } catch (error) {
        console.warn('检查Lodop状态失败:', error)
      }
    }
    
    return {
      canvasContainer,
      leftPanelCollapsed,
      rightPanelCollapsed,
      loading,
      loadingText,
      toast,
      toggleLeftPanel,
      toggleRightPanel,
      handleAddField,
      handleRemoveField,
      handlePreviewPrint,
      handlePrintTemplate,
      handleCloseEditor,
      handleQuickSave,
      handleImportTemplate,
      handleUndo,
      handleRedo,
      handleZoomChange,
      showToast,
      hideToast
    }
  }
}
</script>

<style scoped>
.template-editor {
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: #f8f9fa;
  font-family: '微软雅黑', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
}

/* 主内容区域 */
.main-content {
  flex: 1;
  display: flex;
  height: calc(100vh - 80px);
  overflow: hidden;
}

/* 侧边面板 */
.left-panel,
.right-panel {
  display: flex;
  flex-direction: column;
  background: #fff;
  border-right: 1px solid #dee2e6;
  transition: all 0.3s ease;
  box-shadow: 0 0 10px rgba(0,0,0,0.1);
}

.left-panel {
  width: 320px;
  border-right: 1px solid #dee2e6;
}

.right-panel {
  width: 300px;
  border-left: 1px solid #dee2e6;
}

.left-panel.collapsed {
  width: 48px;
}

.right-panel.collapsed {
  width: 48px;
}

.left-panel.collapsed .panel-title,
.right-panel.collapsed .panel-title {
  display: none;
}

.left-panel.collapsed .panel-header,
.right-panel.collapsed .panel-header {
  justify-content: center;
  padding: 12px 8px;
}

.left-panel.collapsed .collapse-btn,
.right-panel.collapsed .collapse-btn {
  margin: 0;
  width: 32px;
  height: 32px;
  min-width: 32px;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
  border-bottom: 1px solid rgba(255,255,255,0.1);
}

.panel-title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  font-size: 16px;
  font-weight: 600;
}

.title-icon {
  font-size: 18px;
}

.collapse-btn {
  background: rgba(255,255,255,0.2);
  border: 1px solid rgba(255,255,255,0.3);
  color: #fff;
  border-radius: 4px;
  padding: 4px 8px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  min-height: 24px;
}

.collapse-btn:hover {
  background: rgba(255,255,255,0.3);
  border-color: rgba(255,255,255,0.5);
  transform: scale(1.1);
}

.collapse-btn:active {
  transform: scale(0.95);
}

.panel-content {
  flex: 1;
  overflow: hidden;
}

/* 中间画布区域 */
.center-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: #f5f5f5;
  min-width: 0;
}

/* 浮动操作按钮 */
.floating-actions {
  position: fixed;
  bottom: 24px;
  right: 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  z-index: 1000;
}

/* 提示消息 */
.toast {
  position: fixed;
  top: 24px;
  right: 24px;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 20px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  z-index: 2000;
  min-width: 300px;
  max-width: 500px;
}

.toast.success {
  background: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}

.toast.error {
  background: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
}

.toast.warning {
  background: #fff3cd;
  color: #856404;
  border: 1px solid #ffeaa7;
}

.toast.info {
  background: #d1ecf1;
  color: #0c5460;
  border: 1px solid #bee5eb;
}

.toast-icon {
  font-size: 18px;
  flex-shrink: 0;
}

.toast-message {
  flex: 1;
  font-weight: 500;
}

.toast-close {
  background: none;
  border: none;
  font-size: 16px;
  cursor: pointer;
  opacity: 0.7;
  padding: 0;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.toast-close:hover {
  opacity: 1;
}

/* 加载遮罩 */
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3000;
  backdrop-filter: blur(2px);
}

.loading-spinner {
  background: #fff;
  padding: 32px;
  border-radius: 12px;
  text-align: center;
  box-shadow: 0 8px 32px rgba(0,0,0,0.3);
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 16px;
}

.loading-text {
  color: #495057;
  font-weight: 500;
}

/* 动画 */
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.toast-enter-active,
.toast-leave-active {
  transition: all 0.3s ease;
}

.toast-enter-from {
  transform: translateX(100%);
  opacity: 0;
}

.toast-leave-to {
  transform: translateX(100%);
  opacity: 0;
}

.loading-enter-active,
.loading-leave-active {
  transition: all 0.3s ease;
}

.loading-enter-from,
.loading-leave-to {
  opacity: 0;
}

/* 响应式设计 */
@media (max-width: 1200px) {
  .left-panel {
    width: 280px;
  }
  
  .right-panel {
    width: 260px;
  }
}

@media (max-width: 768px) {
  .main-content {
    flex-direction: column;
  }
  
  .left-panel,
  .right-panel {
    width: 100%;
    height: 200px;
    border: none;
    border-bottom: 1px solid #dee2e6;
  }
  
  .left-panel.collapsed,
  .right-panel.collapsed {
    height: 48px;
  }
  
  .floating-actions {
    bottom: 16px;
    right: 16px;
  }
  
  .toast {
    top: 16px;
    right: 16px;
    left: 16px;
    max-width: none;
  }
}

/* 滚动条样式 */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
}

::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
</style>
